interface ExactDimension {
  width: number;
  height: number;
}

/** The creative formats a Gen Single Image placement can call for. */
export type CreativeFormat = 'landscape' | 'square' | 'portrait';

interface FormatSpec {
  /** Exact Demand Gen sizes for this format, most-preferred first. */
  readonly sizes: readonly ExactDimension[];
  /** Nominal aspect ratio, used by the tolerant fallback pass. */
  readonly ratio: number;
  /** Short form, for slots too small to fit the pixel label. */
  readonly shortLabel: string;
}

/**
 * Routes uploaded Gen Single Image creatives to the right preview slot by their
 * actual pixel dimensions rather than by upload order — "Add image" lets several
 * be uploaded in any order, and `PreviewBlockHelper.collectFromBlock` drops the
 * empty ones, so index 1 isn't reliably the landscape (or square) one. The sizes
 * matched here mirror the ones `image-upload.component.ts` allows for
 * `GOOGLE_GEN_SINGLE_IMAGE` (1200x628, 1200x1200, 960x1200, 600x600). The
 * Performance Max image block also uses {@link formatOf} to count its images per size.
 *
 * Dimensions can't be read off a URL synchronously, so an unmeasured image kicks
 * off an async load and the caller sees a match once change detection runs again.
 * That handoff relies on zone.js patching the image's load event — if this app
 * ever goes zoneless, every caller breaks at once and will need an explicit
 * markForCheck. Measured dimensions are cached by URL so re-renders don't refetch.
 *
 * The cache is keyed by URL and never invalidated, so re-uploading a different
 * image to an identical Azure path would serve the old dimensions. Blob URLs are
 * unique per file, so this can't bite before a template is saved.
 */
export class CreativeImageRouterHelper {
  private static readonly FORMATS: Record<CreativeFormat, FormatSpec> = {
    landscape: {
      sizes: [{ width: 1200, height: 628 }],
      ratio: 1200 / 628,
      shortLabel: '1.91:1',
    },
    square: {
      sizes: [
        { width: 1200, height: 1200 },
        { width: 600, height: 600 },
      ],
      ratio: 1,
      shortLabel: '1:1',
    },
    portrait: {
      sizes: [{ width: 960, height: 1200 }],
      ratio: 960 / 1200,
      shortLabel: '4:5',
    },
  };

  /** Wide enough to keep 1.91:1, 1:1 and 4:5 mutually exclusive by a long way. */
  private static readonly RATIO_TOLERANCE = 0.02;

  private static readonly measured = new Map<string, ExactDimension>();
  private static readonly inFlight = new Set<string>();
  /** URLs whose load errored. Without this, a 404 is retried once per CD cycle. */
  private static readonly failed = new Set<string>();

  /**
   * The uploaded image for `format`, or '' when it hasn't been uploaded — or
   * hasn't been measured yet, since the first call for a URL always misses.
   *
   * Exact sizes win first. Anything left over is matched on aspect ratio, which
   * catches creatives saved before upload validation existed, or replaced
   * out-of-band, that are the right shape but not one of the four exact sizes.
   */
  static resolve(images: string[], format: CreativeFormat): string {
    const spec = this.FORMATS[format];
    for (const target of spec.sizes) {
      const match = images.find((url) => this.matchesExact(url, target));
      if (match) {
        return match;
      }
    }
    return images.find((url) => this.matchesRatio(url, spec.ratio)) ?? '';
  }

  /**
   * True while any candidate is still being measured. Callers show a neutral
   * slot rather than the "upload this size" placeholder until it goes false, so
   * the first paint reads as loading instead of as a missing asset.
   */
  static isResolving(images: string[]): boolean {
    return images.some((url) => this.inFlight.has(url));
  }

  /**
   * The format a single image falls into, or undefined while it is still being
   * measured or matches none. Same exact-then-ratio order as {@link resolve}.
   */
  static formatOf(url: string): CreativeFormat | undefined {
    const formats = Object.keys(this.FORMATS) as CreativeFormat[];
    return (
      formats.find((f) => this.FORMATS[f].sizes.some((size) => this.matchesExact(url, size))) ??
      formats.find((f) => this.matchesRatio(url, this.FORMATS[f].ratio))
    );
  }

  /** e.g. "1200x1200 or 600x600" — shown in place of a creative that isn't uploaded. */
  static labelFor(format: CreativeFormat): string {
    return this.FORMATS[format].sizes.map((d) => this.formatDimension(d)).join(' or ');
  }

  /** e.g. "1:1" — for thumbnail slots too narrow for the full pixel label. */
  static shortLabelFor(format: CreativeFormat): string {
    return this.FORMATS[format].shortLabel;
  }

  private static formatDimension(d: ExactDimension): string {
    return `${d.width}x${d.height}`;
  }

  private static matchesExact(url: string, target: ExactDimension): boolean {
    const dimensions = this.dimensionsOf(url);
    return !!dimensions && dimensions.width === target.width && dimensions.height === target.height;
  }

  private static matchesRatio(url: string, ratio: number): boolean {
    const dimensions = this.dimensionsOf(url);
    if (!dimensions || dimensions.height === 0) {
      return false;
    }
    return Math.abs(dimensions.width / dimensions.height - ratio) <= this.RATIO_TOLERANCE;
  }

  private static dimensionsOf(url: string): ExactDimension | undefined {
    if (!url || this.failed.has(url)) {
      return undefined;
    }
    const cached = this.measured.get(url);
    if (cached || this.inFlight.has(url)) {
      return cached;
    }
    this.inFlight.add(url);
    const img = new Image();
    img.onload = () => {
      this.measured.set(url, { width: img.naturalWidth, height: img.naturalHeight });
      this.inFlight.delete(url);
    };
    img.onerror = () => {
      // Remember the failure, otherwise the next change-detection pass sees an
      // unmeasured, not-in-flight URL and fires another request, forever.
      this.inFlight.delete(url);
      this.failed.add(url);
    };
    img.src = url;
    return undefined;
  }
}
